/**
 * New Task — a title and Enter, with the full form one click away (#33).
 *
 * The owner asked for "a quick-create mode: allow Title + Enter with contextual
 * defaults, with 'More details' opening the full form". Before this, raising a task
 * meant a thirteen-field window with two required boxes, for work that is usually one
 * sentence somebody wants written down before they forget it.
 *
 * **The defaults are the board's own** (#42): the client, queue and person the reader
 * has filtered to, through `createDefaultsFromFacets`. So a quick task raised while
 * looking at Casaturca's content queue is Casaturca's content task, and the field
 * nobody filled in is the one the board already answered.
 *
 * Both modes are the same `ActionDialog` over the same action; quick create draws
 * `only` the title and carries the rest as `fixed`, so there is no second form and no
 * second set of defaults. Switching to More details is a remount with the same
 * values, which is what `ActionDialog`'s `key` on `open` already does for every
 * dialog.
 */

import { useState } from 'react'
import { z } from 'zod'

import { Button } from '../../../components/portal/Button.js'
import type { FormValues } from '../../../components/portal/schemaForm.js'
import { Muted, Small } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { assigneeBody } from '../../../components/studio/ActionForm.js'
import { NOBODY } from '../../../components/studio/ActionField.js'
import { QUEUE_WORD, priorityWord } from '../../../components/studio/taskWords.js'
import { STUDIO_COLUMN } from './board.js'

const CreatedSchema = z.object({ task: z.object({ ref: z.string() }) })

/**
 * Only Title and References span both columns (#39/#40) — What to do and Done
 * when pair on one row instead, per `actionFields.ts` `CREATE_DIALOG_ORDER`.
 */
const CREATE_FULL_WIDTH = ['title', 'context_refs'] as const

/** The queue a task gets when the board the reader is on does not name one. */
const FALLBACK_QUEUE = 'delivery'

/**
 * What the quick path will produce, as a sentence — "Casaturca · Delivery · Unassigned".
 *
 * Read off the very object the form is given, so the line cannot describe one set of
 * defaults while the window sends another. The client is the board's facet, which is
 * a **slug** — and a slug is not what anybody calls the company: the line read
 * "Creates in casaturca" beside a full form that said "Casaturca". `clientName` is
 * the page's own lookup over the columns it has already loaded, so no second read is
 * opened to say a name this page is holding.
 */
function defaultsLine(
  defaults: Readonly<Record<string, unknown>>,
  meName: string | null,
  clientName: (slug: string) => string,
): string {
  const client =
    typeof defaults['client'] === 'string' ? clientName(defaults['client']) : STUDIO_COLUMN
  const queue = typeof defaults['queue'] === 'string' ? defaults['queue'] : FALLBACK_QUEUE
  const assignee = defaults['assignee']
  const who =
    assignee === undefined || assignee === NOBODY
      ? 'Unassigned'
      : (meName ?? 'whoever the board is filtered to')
  const priority =
    typeof defaults['priority'] === 'number' ? priorityWord(defaults['priority']) : null
  return [client, QUEUE_WORD[queue as keyof typeof QUEUE_WORD], who, priority]
    .filter((one): one is string => one !== null)
    .join(' · ')
}

export type QuickCreateProps = {
  /** Facet-derived defaults (#42) — queue falls back to delivery when unset. */
  readonly defaults: Readonly<Record<string, unknown>>
  /** The reader's own name, for the line that says who the task will go to. */
  readonly meName?: string | null | undefined
  /** A client slug as the company is called. The page already has the directory. */
  readonly clientName?: ((slug: string) => string) | undefined
  /**
   * Whether the window is up, held by the page.
   *
   * It used to be this component's own state, and the `n` shortcut had to reach
   * through the document for a button whose text was "New Task" to press it. The
   * page owns the flag now, which is what lets a shortcut open the window by saying
   * so (#51).
   */
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly onDone: (ref: string) => void
}

/** The answers somebody actually gave — a blank box is not one. */
function filled(values: FormValues): FormValues {
  const out: FormValues = {}
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === '') continue
    out[key] = value
  }
  return out
}

export function QuickCreate({
  defaults,
  meName,
  clientName,
  open,
  onOpenChange,
  onDone,
}: QuickCreateProps) {
  const queue = defaults['queue']
  /*
   * What the quick window has been told so far, carried across to the full form.
   *
   * "More details" remounts the dialog with `defaults`, and a title typed in the
   * quick box was not in them — so the full form opened with an empty Title and the
   * sentence somebody had just written was gone.
   */
  const [carried, setCarried] = useState<FormValues>({})
  const formDefaults = {
    ...defaults,
    queue: typeof queue === 'string' ? queue : FALLBACK_QUEUE,
  }
  const fullDefaults = { ...formDefaults, ...filled(carried) }
  /*
   * What the quick window sends as it stands, because it draws only the title.
   *
   * The assignee is the full form's **box value** — `"user:<id>"`, or `"none"` — which
   * `bodyOf` turns into the API's pair for a box that is drawn. Carried undrawn it went
   * out raw, as an `assignee` key `tasks_create`'s strict input refuses, so no task
   * could be raised from the quick window while the board's Who was "me" or
   * "Unassigned". `assigneeBody` is `bodyOf`'s own split, so the two windows send the
   * same pair for the same choice.
   */
  const { assignee, ...rest }: Readonly<Record<string, unknown>> = formDefaults
  const quickFixed =
    typeof assignee === 'string' ? { ...rest, ...assigneeBody(assignee) } : formDefaults
  const name = clientName ?? ((slug: string) => slug)

  const [full, setFull] = useState(false)

  const done = (output: unknown): void => {
    onOpenChange(false)
    setFull(false)
    setCarried({})
    onDone(CreatedSchema.parse(output).task.ref)
  }

  if (full) {
    return (
      <ActionDialog
        action="tasks_create"
        label="New Task"
        title="New Task"
        submitLabel="Create task"
        variant="primary"
        wide
        columns={2}
        fullWidth={CREATE_FULL_WIDTH}
        defaults={fullDefaults}
        /*
         * The board's filters, as a starting point: nothing is stored behind this
         * form. So a box the reader empties — Client, back to "Internal" — is a field
         * left out, which is what no client *is* to `tasks_create`. Unsaid, the form
         * would treat the filter as a stored value being taken away and refuse it.
         */
        suggested
        // What was typed in the quick window is unsaved input in this one too.
        unsavedAtOpen={Object.keys(filled(carried)).length > 0}
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next)
          if (!next) {
            setFull(false)
            setCarried({})
          }
        }}
        onDone={done}
      />
    )
  }

  return (
    <>
      <Button
        variant="primary"
        onClick={() => {
          onOpenChange(true)
        }}
      >
        New Task
      </Button>
      <ActionDialog
        action="tasks_create"
        label="New Task"
        title="New Task"
        submitLabel="Create task"
        size="sm"
        variant="primary"
        only={['title']}
        autoFocusFirstField
        fixed={quickFixed}
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next)
          if (!next) setCarried({})
        }}
        onDone={done}
        beside={(values, working) => (
          /*
           * The way out of the quick path, **beside the commit**. It used to be
           * `children`, which `ActionForm` draws above the fields — so a button that
           * leaves the form looked like one more box to fill in.
           *
           * It is handed what the quick form holds, and carries it over: the full
           * form opens on the title that has already been typed.
           */
          <Button
            size="sm"
            // Not while the title is on its way: this takes the form away, and its answer with it.
            disabled={working}
            onClick={() => {
              setCarried(values)
              setFull(true)
            }}
          >
            More details
          </Button>
        )}
      >
        {/*
          What the title alone will produce, in one line (#33). The quick path hides
          every other field, and a reader who cannot see the client and the queue it
          is about to use has to open the full form to find out — which is the trip
          this mode exists to save.
        */}
        <Small>
          <Muted>{`Creates in ${defaultsLine(formDefaults, meName ?? null, name)}`}</Muted>
        </Small>
      </ActionDialog>
    </>
  )
}
